import { FC, useEffect, useRef } from 'react';
import {
  AutocompleteInput,
  Button,
  DataLossPrompt,
  FileInput,
  Footer,
  ImageList,
  Panel,
  SelectInput,
  TextField,
} from 'melp-design/components';
import { Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Controller, SubmitHandler, useForm } from 'react-hook-form-latest';
import { APP, ROUTES } from 'config';
import { useRouteParams } from 'utils/useIdParam';
import { usePredefinedToasts } from 'utils/Toast';
import {
  BenefitTemplateStatus,
  benefitTemplateStatuses,
} from 'store/benefit-templates';
import {
  useAdminBenefitTemplate,
  useAdminUpdateBenefitTemplate,
} from 'store/admin-benefit-templates';
import { benefitCategories, BenefitCategory } from 'store/benefits';
import { formatCountry } from 'utils/format';
import { useUploadFile } from 'store/files';
import { Delete } from '@mui/icons-material';

interface Values {
  name: string;
  status: BenefitTemplateStatus;
  category: BenefitCategory;
  thumbnailId: string;
  supportedCountries: string[];
}

const INITIAL_VALUES: Values = {
  name: '',
  status: 'inactive',
  category: benefitCategories[0],
  thumbnailId: '',
  supportedCountries: [],
};

export const BenefitTemplateSettings: FC = () => {
  const { t, i18n } = useTranslation();
  const predefinedToasts = usePredefinedToasts();
  const formRef = useRef<HTMLFormElement>(null);
  const { id: benefitTemplateId } = useRouteParams(
    ROUTES.admin.benefitTemplates.details,
  );

  const { data: template } = useAdminBenefitTemplate(benefitTemplateId);

  const { mutate: uploadFile, isLoading: isUploading } = useUploadFile();
  const { mutate: updateTemplate, isLoading: isUpdating } =
    useAdminUpdateBenefitTemplate();

  const {
    handleSubmit,
    watch,
    reset,
    control,
    formState: { dirtyFields },
  } = useForm<Values>({
    defaultValues: INITIAL_VALUES,
  });

  useEffect(() => {
    if (template) {
      reset({
        name: template.name,
        status: template.status,
        category: template.category,
        thumbnailId: template.thumbnailId,
        supportedCountries: template.supportedCountries,
      });
    }
  }, [template, reset]);

  const onSubmit: SubmitHandler<Values> = (values) => {
    updateTemplate(
      {
        benefitTemplateId,
        data: {
          name: values.name,
          status: values.status,
          category: values.category,
          thumbnailId: values.thumbnailId || null,
          supportedCountries: values.supportedCountries,
        },
      },
      {
        onSuccess: () => {
          predefinedToasts.success.updated();
        },
      },
    );
  };

  const isDirty = !!Object.keys(dirtyFields).length;
  const [thumbnailId] = watch(['thumbnailId']);

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} ref={formRef}>
        <Stack gap={2}>
          <Panel title={t('common.settings')}>
            <Stack spacing={2}>
              <Controller
                name="status"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => {
                  return (
                    <SelectInput
                      label={t('common.status')}
                      name={name}
                      value={value}
                      onChange={onChange}
                      options={benefitTemplateStatuses.map((status) => ({
                        label: t(`status.${status}`),
                        value: status,
                      }))}
                      required
                      ref={ref}
                    />
                  );
                }}
              />

              <Controller
                name="name"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => {
                  return (
                    <TextField
                      label={t('common.systemName')}
                      name={name}
                      value={value}
                      onChange={onChange}
                      required
                      ref={ref}
                    />
                  );
                }}
              />

              <Controller
                name="category"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => (
                  <SelectInput
                    label={t('marketplaceItems.benefitPlanTypes')}
                    name={name}
                    value={value}
                    onChange={onChange}
                    options={benefitCategories.map((category) => ({
                      value: category,
                      label: t(`menu.${category}`),
                    }))}
                    required
                    ref={ref}
                  />
                )}
              />

              <Controller
                name="supportedCountries"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => (
                  <AutocompleteInput
                    label={t('benefitTemplates.supportedCountries')}
                    name={name}
                    value={value}
                    onChange={onChange}
                    options={APP.countries.alpha3.map((alpha3) => ({
                      value: alpha3,
                      label: formatCountry(i18n.language, alpha3),
                    }))}
                    required
                    multiple
                    ref={ref}
                  />
                )}
              />

              <Controller
                name="thumbnailId"
                control={control}
                render={({ field: { name, onChange, ref } }) => {
                  return thumbnailId ? (
                    <ImageList
                      items={[
                        {
                          imageId: thumbnailId,
                          actions: [
                            {
                              icon: Delete,
                              variant: 'neutral-outline',
                              onClick: () => onChange(''),
                            },
                          ],
                        },
                      ]}
                    />
                  ) : (
                    <FileInput
                      label={t('benefitTemplates.thumbnail')}
                      name={name}
                      onChange={([file]) => {
                        if (file) {
                          uploadFile(
                            { file },
                            {
                              onSuccess: ({ data }) => {
                                onChange(data.id);
                              },
                            },
                          );
                        } else {
                          onChange('');
                        }
                      }}
                      disabled={isUploading}
                      maxFiles={1}
                      previewSelectedFiles={false}
                      accept={['jpg', 'jpeg', 'png', 'gif', 'webp']}
                      ref={ref}
                    />
                  );
                }}
              />
            </Stack>
          </Panel>
        </Stack>

        <Footer visible={isDirty}>
          <Stack direction="row" justifyContent="end" gap={1}>
            <Button
              label={t('common.cancel')}
              variant="neutral-outline"
              onClick={() => {
                reset(undefined, { keepDirty: false });
              }}
            />

            <Button
              label={t('common.save')}
              variant="primary"
              type="submit"
              disabled={isUpdating}
            />
          </Stack>
        </Footer>
      </form>

      <DataLossPrompt when={isDirty} />
    </>
  );
};
